<template>
  <v-container fluid class="os">

    <!-- zaglavlje -->
    <div class="os-head">
      <div class="os-firma">
        <div class="os-firma-ime">{{ comp.CompanyNameShort }}</div>
        <div>{{ comp.CompanyStreet }} {{ comp.CompanyBuildingNameNumber }}</div>
        <div>{{ comp.CompanyPostcode }} {{ comp.CompanyCity }}</div>
        <div>OIB: {{ comp.CompanyVATID }}</div>
      </div>
      <div class="os-naslov">
        <h2>Dnevnik knjiženja</h2>
        <div class="os-meta">
          {{ currency }}<span class="os-sep">·</span>{{ razdobljeTxt }}<span class="os-sep">·</span>poslovna godina {{ godina }}
        </div>
      </div>
      <div class="os-akcije">
        <v-btn small outlined color="primary" :disabled="!ucitano" @click="printaj(false)">Printaj</v-btn>
        <v-btn small outlined :disabled="!ucitano" :loading="pripremaDetalja" @click="printaj(true)">Printaj detaljno</v-btn>
        <v-btn small outlined :disabled="!ucitano" :loading="pripremaExcel" @click="uExcel">Excel</v-btn>
      </div>
    </div>

    <v-progress-linear v-if="!ucitano && !greska" indeterminate color="primary" class="mt-2"></v-progress-linear>
    <div v-if="greska" class="dn-greska" role="alert">{{ greska }}</div>

    <template v-if="ucitano">
      <!-- sazetak -->
      <div class="os-sazetak">
        <div class="os-kut">
          <span>Dokumenata</span>
          <b>{{ zbroj.dokumenata }}</b>
          <small>{{ zbroj.stavaka }} {{ oblik(zbroj.stavaka, 'stavka', 'stavke', 'stavaka') }}<template v-if="zbroj.dokumenata !== dokumenti.length"> · od ukupno {{ dokumenti.length }}</template></small>
        </div>
        <div class="os-kut">
          <span>Duguje</span>
          <b>{{ formatPrice(zbroj.duguje) }}</b>
        </div>
        <div class="os-kut">
          <span>Potražuje</span>
          <b>{{ formatPrice(zbroj.potrazuje) }}</b>
        </div>
        <div class="os-kut" :class="zbroj.neuravnotezeno ? 'os-lose' : 'os-istaknuto'">
          <span>Razlika</span>
          <b>{{ zbroj.neuravnotezeno ? formatPrice(Math.abs(zbroj.razlika)) : 'u ravnoteži' }}</b>
        </div>
        <div class="os-kut" :class="{'os-lose': neuravnotezenihUkupno > 0}">
          <span>Neuravnoteženih dokumenata</span>
          <b v-if="neuravnotezenihUkupno === 0">nema</b>
          <button v-else type="button" class="dn-link" @click="samoNeuravnotezeni = !samoNeuravnotezeni">
            {{ neuravnotezenihUkupno }}
          </button>
          <small v-if="neuravnotezenihUkupno > 0">{{ samoNeuravnotezeni ? 'prikazani su samo oni' : 'klik ih izdvaja' }}</small>
        </div>
      </div>

      <!-- alati -->
      <div class="os-alati">
        <span class="os-oznaka">Vrsta</span>
        <div class="os-seg" role="group" aria-label="Vrsta dokumenta">
          <button v-for="v in vrste" :key="v.k" type="button" :aria-pressed="String(vrsta === v.k)" @click="vrsta = v.k">
            {{ v.naziv }} <span class="dn-broj">{{ v.broj }}</span>
          </button>
        </div>
        <span class="os-oznaka">Redoslijed</span>
        <div class="os-seg" role="group" aria-label="Redoslijed">
          <button type="button" :aria-pressed="String(redoslijed === 'knjizenje')" @click="redoslijed = 'knjizenje'">Datum knjiženja</button>
          <button type="button" :aria-pressed="String(redoslijed === 'unos')" @click="redoslijed = 'unos'">Vrijeme unosa</button>
        </div>
        <label class="dn-kvacica">
          <input v-model="samoNeuravnotezeni" type="checkbox">
          samo neuravnoteženi
        </label>
        <input v-model="trazi" class="os-trazi dn-trazi" type="search" placeholder="broj, partner, opis ili tko je knjižio" aria-label="Traži dokument">
      </div>

      <div v-if="prikaz.length === 0" class="os-prazno">
        {{ dokumenti.length === 0 ? 'U odabranom razdoblju nema proknjiženih dokumenata.' : 'Nijedan dokument ne odgovara odabranim uvjetima.' }}
      </div>

      <div v-else class="os-scroll dn-scroll">
        <table class="os-tbl dn-tbl">
          <thead>
            <tr>
              <th>Rb.</th>
              <th class="l">Datum knjiž.</th>
              <th class="l">Vrsta</th>
              <th class="l">Dokument</th>
              <th class="l">Partner / opis</th>
              <th>Stavaka</th>
              <th>Duguje</th>
              <th>Potražuje</th>
              <th class="l">Knjižio</th>
              <th><span class="dn-sr">Stavke</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="d in vidljivi">
              <tr
                :key="'d' + d.id"
                class="os-klik dn-dok"
                :class="{otvoren: otvoreni[d.id]}"
                tabindex="0"
                :aria-expanded="String(!!otvoreni[d.id])"
                @click="prebaci(d)"
                @keydown.enter.prevent="prebaci(d)"
              >
                <td class="num dn-rb">{{ d.rb }}</td>
                <td class="doc">{{ d.datumTxt }}</td>
                <td><span class="os-tag" :class="'dn-' + d.vrsta.grupa">{{ d.vrsta.naziv }}</span></td>
                <td class="doc dn-oznaka">
                  {{ d.oznaka }}
                  <span v-if="d.neuravnotezen" class="dn-cip">razlika {{ formatPrice(Math.abs(d.razlika)) }}</span>
                </td>
                <td class="opis">
                  <b v-if="d.partnerName">{{ d.partnerName }}</b><span v-if="d.partnerName && d.opis" class="dn-sep"> · </span>{{ d.opis }}
                </td>
                <td class="num dana">{{ d.stavaka }}</td>
                <td class="num">{{ formatPrice(d.duguje) }}</td>
                <td class="num">{{ formatPrice(d.potrazuje) }}</td>
                <td class="dn-knjizio">
                  <span>{{ d.knjizio }}</span>
                  <small>{{ d.unesenoTxt }}</small>
                </td>
                <td class="dn-strelica-celija"><span class="dn-strelica" :class="{otvoren: otvoreni[d.id]}" aria-hidden="true">▶</span></td>
              </tr>

              <tr v-if="otvoreni[d.id]" :key="'s' + d.id" class="dn-detalj">
                <td colspan="10">
                  <div v-if="!stavke[d.id]" class="dn-ucitava">Učitavam stavke…</div>
                  <div v-else-if="stavke[d.id].greska" class="dn-greska" role="alert">{{ stavke[d.id].greska }}</div>
                  <template v-else>
                    <table class="dn-stavke">
                      <thead>
                        <tr>
                          <th class="l">Konto</th>
                          <th class="l">Naziv konta</th>
                          <th class="l">Opis</th>
                          <th class="l">Partner</th>
                          <th class="l">Veza</th>
                          <th>Duguje</th>
                          <th>Potražuje</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="s in stavke[d.id].redci" :key="s.id">
                          <td class="doc">{{ s.account4 }}</td>
                          <td class="opis">{{ s.naziv }}</td>
                          <td class="opis">{{ s.Description }}</td>
                          <td class="opis">{{ s.partnerName }}</td>
                          <td class="doc">{{ s.veza }}</td>
                          <td class="num" :class="{nula: !s.duguje}">{{ s.duguje ? formatPrice(s.duguje) : '–' }}</td>
                          <td class="num" :class="{nula: !s.potrazuje}">{{ s.potrazuje ? formatPrice(s.potrazuje) : '–' }}</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colspan="5">
                            Ukupno {{ d.oznaka }}
                            <span v-if="d.neuravnotezen" class="dn-upoz"> · dokument nije u ravnoteži, razlika {{ formatPrice(Math.abs(d.razlika)) }}</span>
                          </td>
                          <td class="num">{{ formatPrice(d.duguje) }}</td>
                          <td class="num">{{ formatPrice(d.potrazuje) }}</td>
                        </tr>
                      </tfoot>
                    </table>
                    <div class="dn-meta-dok">
                      Knjižio {{ d.knjizio }}<template v-if="d.email && d.email !== d.knjizio"> ({{ d.email }})</template>
                      · uneseno {{ d.unesenoTxt }}<template v-if="d.promijenjenoTxt && d.promijenjenoTxt !== d.unesenoTxt"> · zadnja promjena {{ d.promijenjenoTxt }}</template>
                    </div>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5">UKUPNO {{ currency }}<span v-if="zbroj.dokumenata !== dokumenti.length" class="os-napomena"> · samo prikazani dokumenti</span></td>
              <td class="num">{{ zbroj.stavaka }}</td>
              <td class="num">{{ formatPrice(zbroj.duguje) }}</td>
              <td class="num">{{ formatPrice(zbroj.potrazuje) }}</td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-if="prikaz.length > vidljivi.length" class="dn-jos">
        <v-btn small outlined @click="prikazano += korak">Prikaži još {{ Math.min(korak, prikaz.length - vidljivi.length) }}</v-btn>
        <span>prikazano {{ vidljivi.length }} od {{ prikaz.length }} dokumenata · zbrojevi gore vrijede za sve</span>
      </div>
    </template>
  </v-container>
</template>

<script>
// Dnevnik knjizenja - proknjizeni dokumenti kronoloski, jedan redak po
// dokumentu, s ukupno duguje/potrazuje i tko je dokument unio. Klik na
// dokument otvara njegove stavke (ucitavaju se tek tada).
//
// Pokrece se iz MainLedgerAnalysis gumbom Dnevnik, s datumima iz analize.

import dayjs from 'dayjs'
import MainLedgerDnevnikService from '@/services/MainLedgerDnevnikService'
import {mnozina} from './otvoreneStavke'
import {printJournalPdf} from './mainLedgerPdf'
import {exportJournalXlsx} from './mainLedgerXlsx'
import { domacaValuta } from '@/snippets/domacaValuta'

const n2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100

// Grupa za filtar i oznaka na ekranu, prema knjizi.
const vrstaDokumenta = (d) => {
  const k = String(d.knjiga || '').toUpperCase()
  if (k === 'BS' || d.MLType === 4) { return {grupa: 'izvod', naziv: 'Izvod'} }
  if (k.indexOf('URA') === 0 || k.indexOf('URE') === 0) { return {grupa: 'ura', naziv: 'URA'} }
  if (k.indexOf('IRA') === 0 || k.indexOf('IRE') === 0) { return {grupa: 'ira', naziv: 'IRA'} }
  if (k === 'TEM') { return {grupa: 'tem', naziv: 'Temeljnica'} }
  if (k === 'PS' || k === 'PSB') { return {grupa: 'ostalo', naziv: 'Poč. stanje'} }
  return {grupa: 'ostalo', naziv: d.knjiga || 'Ostalo'}
}

const GRUPE = [
  {k: 'sve', naziv: 'Sve'},
  {k: 'ura', naziv: 'URA'},
  {k: 'ira', naziv: 'IRA'},
  {k: 'izvod', naziv: 'Izvodi'},
  {k: 'tem', naziv: 'Temeljnice'},
  {k: 'ostalo', naziv: 'Ostalo'}
]

// Broj racuna na koji se stavka odnosi, s godinom racuna.
const vezaStavke = (s) => {
  const inNr = s.invoiceInNumber ? String(s.invoiceInNumber).trim() : ''
  const outNr = s.invoiceOutNumber ? String(s.invoiceOutNumber).trim() : ''
  let broj = String(s.account4 || '').substring(0, 2) === '12' ? outNr : inNr
  if (!broj || broj === '0') { broj = outNr && outNr !== '0' ? outNr : (inNr !== '0' ? inNr : '') }
  if (!broj || broj === '0') { return '' }
  return broj + (s.BusinessYearInvoice ? '/' + parseInt(s.BusinessYearInvoice) : '')
}

const poruka = (err) => {
  if (err && err.response && err.response.data && err.response.data.error) { return err.response.data.error }
  return err && err.message ? err.message : String(err)
}

export default {
  name: 'MainLedgerDnevnik',
  props: ['head', 'showEUR'],
  data () {
    return {
      comp: {},
      currency: 'EUR',
      exRate: 1,
      ucitano: false,
      greska: '',
      dokumenti: [],
      vrsta: 'sve',
      redoslijed: 'knjizenje',
      samoNeuravnotezeni: false,
      trazi: '',
      korak: 400,
      prikazano: 400,
      otvoreni: {},
      stavke: {},
      pripremaDetalja: false,
      pripremaExcel: false
    }
  },
  computed: {
    godina () {
      return parseInt(this.head.BusinessYear) || parseInt(this.$store.state.businessYear) ||
        (this.head.docToDate ? dayjs(this.head.docToDate).year() : dayjs().year())
    },
    razdobljeTxt () {
      const od = this.head.docFromDate ? dayjs(this.head.docFromDate).format('DD.MM.YYYY') : '01.01.' + this.godina
      const doo = this.head.docToDate ? dayjs(this.head.docToDate).format('DD.MM.YYYY') : '31.12.' + this.godina
      return od + ' – ' + doo
    },
    vrste () {
      return GRUPE
        .map(g => ({...g, broj: g.k === 'sve' ? this.dokumenti.length : this.dokumenti.filter(d => d.vrsta.grupa === g.k).length}))
        .filter(g => g.k === 'sve' || g.broj > 0)
    },
    prikaz () {
      const t = this.trazi.trim().toLowerCase()
      let r = this.dokumenti
      if (this.vrsta !== 'sve') { r = r.filter(d => d.vrsta.grupa === this.vrsta) }
      if (this.samoNeuravnotezeni) { r = r.filter(d => d.neuravnotezen) }
      if (t) { r = r.filter(d => d.pretraga.indexOf(t) !== -1) }
      if (this.redoslijed === 'unos') {
        r = [...r].sort((a, b) => (a.uneseno > b.uneseno ? 1 : (a.uneseno < b.uneseno ? -1 : a.rb - b.rb)))
      }
      return r
    },
    vidljivi () {
      return this.prikaz.slice(0, this.prikazano)
    },
    zbroj () {
      const z = {dokumenata: this.prikaz.length, stavaka: 0, duguje: 0, potrazuje: 0}
      this.prikaz.forEach(d => {
        z.stavaka += d.stavaka
        z.duguje = n2(z.duguje + d.duguje)
        z.potrazuje = n2(z.potrazuje + d.potrazuje)
      })
      z.razlika = n2(z.duguje - z.potrazuje)
      z.neuravnotezeno = Math.abs(z.razlika) > 0.005
      z.neuravnotezenih = this.prikaz.filter(d => d.neuravnotezen).length
      return z
    },
    neuravnotezenihUkupno () {
      return this.dokumenti.filter(d => d.neuravnotezen).length
    }
  },
  watch: {
    vrsta () { this.prikazano = this.korak },
    redoslijed () { this.prikazano = this.korak },
    samoNeuravnotezeni () { this.prikazano = this.korak },
    trazi () { this.prikazano = this.korak }
  },
  async mounted () {
    this.comp = this.$store.state.companyActiv ? this.$store.state.companyActiv : {}
    this.currency = domacaValuta(this.$store.state.businessYear)
    this.exRate = this.showEUR && this.$store.state.defExRate ? this.$store.state.defExRate : 1
    await this.ucitaj()
  },
  methods: {
    formatPrice (value) {
      const n = parseFloat(value)
      return (isNaN(n) ? 0 : n).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    oblik (n, jedan, dva, pet) {
      return mnozina(n, jedan, dva, pet)
    },
    uvjeti () {
      return {
        CompanyId: this.$store.state.companyid,
        BusinessYear: this.godina,
        odDatuma: this.head.docFromDate || null,
        doDatuma: this.head.docToDate || null
      }
    },
    async ucitaj () {
      try {
        const {data} = await MainLedgerDnevnikService.dokumenti(this.uvjeti())
        const lista = data && data.dokumenti ? data.dokumenti : []
        this.dokumenti = lista.map((d, i) => this.pripremi(d, i))
        this.ucitano = true
      } catch (err) {
        this.greska = 'Dnevnik nije učitan: ' + poruka(err)
      }
    },
    pripremi (d, i) {
      const k = this.exRate || 1
      const duguje = n2(d.duguje / k)
      const potrazuje = n2(d.potrazuje / k)
      const vrsta = vrstaDokumenta(d)
      const oznaka = ((vrsta.grupa === 'izvod' ? 'Izv.' : d.knjiga) + ' ' + d.broj).trim()
      const format = (v) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '')
      return {
        ...d,
        rb: i + 1,
        vrsta: vrsta,
        oznaka: oznaka,
        duguje: duguje,
        potrazuje: potrazuje,
        razlika: n2(duguje - potrazuje),
        neuravnotezen: Math.abs(duguje - potrazuje) > 0.005,
        datumTxt: d.datum ? dayjs(d.datum).format('DD.MM.YYYY') : '',
        unesenoTxt: format(d.uneseno),
        promijenjenoTxt: format(d.promijenjeno),
        pretraga: [oznaka, d.partnerName, d.partnerVATID, d.opis, d.knjizio, d.email, d.InvoiceNumber]
          .join(' ').toLowerCase()
      }
    },
    pripremiStavku (s) {
      const k = this.exRate || 1
      return {...s, duguje: n2(s.Debit / k), potrazuje: n2(s.Credit / k), veza: vezaStavke(s)}
    },
    async prebaci (d) {
      const otvori = !this.otvoreni[d.id]
      this.$set(this.otvoreni, d.id, otvori)
      if (!otvori || this.stavke[d.id]) { return }
      try {
        const {data} = await MainLedgerDnevnikService.stavke({CompanyId: this.$store.state.companyid, MainLedgerId: d.id})
        const redci = data && data.stavke ? data.stavke : []
        this.$set(this.stavke, d.id, {redci: redci.map(s => this.pripremiStavku(s))})
      } catch (err) {
        this.$set(this.stavke, d.id, {greska: 'Stavke nisu učitane: ' + poruka(err)})
      }
    },

    // Ispis i Excel prate ekran: ista vrsta, pretraga i redoslijed.
    zaIzvoz () {
      const g = GRUPE.find(x => x.k === this.vrsta)
      const filtar = [
        this.vrsta !== 'sve' && g ? 'vrsta: ' + g.naziv : '',
        this.samoNeuravnotezeni ? 'samo neuravnoteženi' : '',
        this.trazi.trim() ? 'traži: „' + this.trazi.trim() + '"' : ''
      ].filter(x => x).join(' · ')
      return {
        company: this.comp,
        valuta: this.currency,
        razdoblje: this.razdobljeTxt,
        godina: this.godina,
        redoslijed: this.redoslijed === 'unos' ? 'po vremenu unosa' : 'po datumu knjiženja',
        filtar: filtar,
        dokumenti: this.prikaz,
        zbroj: this.zbroj
      }
    },
    // Sve stavke razdoblja, po dokumentu.
    async sveStavke () {
      const {data} = await MainLedgerDnevnikService.stavke(this.uvjeti())
      const po = {}
      ;(data && data.stavke ? data.stavke : []).forEach(s => {
        if (!po[s.databaseMLID]) { po[s.databaseMLID] = [] }
        po[s.databaseMLID].push(this.pripremiStavku(s))
      })
      return po
    },
    async printaj (detaljno) {
      if (!detaljno) {
        printJournalPdf(this.zaIzvoz())
        return
      }
      const stavaka = this.zbroj.stavaka
      if (stavaka > 6000 && !window.confirm('Detaljan ispis ima ' + stavaka + ' stavaka i može imati nekoliko stotina stranica. ' +
        'Za toliko je praktičniji Excel ili kraće razdoblje. Svejedno ispisati?')) {
        return
      }
      this.pripremaDetalja = true
      try {
        const po = await this.sveStavke()
        printJournalPdf({...this.zaIzvoz(), detaljno: true, stavke: po})
      } catch (err) {
        this.greska = 'Detaljan ispis nije pripremljen: ' + poruka(err)
      }
      this.pripremaDetalja = false
    },
    async uExcel () {
      this.pripremaExcel = true
      try {
        const po = await this.sveStavke()
        exportJournalXlsx({...this.zaIzvoz(), stavke: po})
      } catch (err) {
        this.greska = 'Excel nije pripremljen: ' + poruka(err)
      }
      this.pripremaExcel = false
    }
  }
}
</script>

<style scoped src="./otvoreneStavke.css"></style>

<style scoped>
.dn-scroll { max-height: 66vh; }

.dn-tbl td.dn-rb { color: #79827e; font-size: 13px; }
.dn-dok td { vertical-align: top; padding-top: 5px; padding-bottom: 5px; }
.dn-dok.otvoren td { background: #f1f5f3; border-bottom-color: transparent; }
.dn-tbl td.opis b { font-weight: 600; color: #191d1c; }
.dn-sep { color: #b6bdb9; }
.dn-oznaka { white-space: nowrap; }
.dn-cip {
  display: block;
  width: fit-content;
  margin-top: 2px;
  padding: 0 5px;
  border: 1px solid #e0b4a8;
  border-radius: 2px;
  color: #8c2f1e;
  font-size: 11px;
  font-weight: 600;
}
.dn-knjizio span { display: block; font-size: 13px; }
.dn-knjizio small { color: #79827e; font-size: 12px; }
.dn-strelica-celija { width: 22px; text-align: center; }
.dn-strelica { display: inline-block; color: #79827e; font-size: 9px; transition: transform .12s ease; }
.dn-strelica.otvoren { transform: rotate(90deg); color: #14534c; }
.dn-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }

.os-tag.dn-ura { border-color: #b9c9e3; color: #2c4a7a; }
.os-tag.dn-ira { border-color: #b7d6c4; color: #1f6b43; }
.os-tag.dn-izvod { border-color: #e7c99a; color: #8c5a1e; }
.os-tag.dn-tem { border-color: #c7cfcb; color: #4a524f; }
.os-tag.dn-ostalo { border-color: #d8cfe3; color: #5b4a7a; }

.dn-broj { color: #79827e; font-size: 11.5px; padding-left: 2px; }
.os-seg button[aria-pressed="true"] .dn-broj { color: #d6e6e2; }
.dn-kvacica { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #4a524f; cursor: pointer; }
.dn-trazi { margin-left: auto; min-width: 260px; }
.dn-link {
  padding: 0;
  border: 0;
  background: none;
  color: #8c2f1e;
  font: inherit;
  font-size: 17px;
  font-weight: 700;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
}

/* stavke otvorenog dokumenta */
.dn-detalj > td { padding: 0 8px 12px 44px; background: #f1f5f3; border-bottom: 1px solid #c7cfcb; }
.os-tbl tbody tr.dn-detalj:hover > td { background: #f1f5f3; }
.dn-stavke { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e2e6e3; }
.dn-stavke thead th {
  position: static;
  padding: 5px 8px;
  background: #fff;
  font-size: 11px;
}
.dn-stavke td { font-size: 13px; }
.os-tbl .dn-stavke tfoot td { position: static; background: #fafbfa; border-top: 1px solid #c7cfcb; }
.dn-upoz { color: #8c2f1e; font-weight: 600; }
.dn-meta-dok { padding-top: 6px; color: #79827e; font-size: 12px; }
.dn-ucitava { padding: 10px 0; color: #79827e; font-size: 13px; }

.dn-greska {
  margin: 8px 0;
  padding: 8px 12px;
  border: 1px solid #e0b4a8;
  border-radius: 2px;
  background: #fbeeea;
  color: #8c2f1e;
}
.dn-jos { display: flex; align-items: center; gap: 12px; padding: 8px 4px; color: #79827e; font-size: 13px; }
</style>
